import { useTranslation } from 'react-i18next';

export interface Country {
  name: string;
  code: string; // 2-letter code (used for SerpApi gl parameter)
}

// Lista estática de países com seus códigos
export const COUNTRIES: Country[] = [
  { name: 'Brasil', code: 'br' },
  { name: 'Estados Unidos', code: 'us' },
  { name: 'Canadá', code: 'ca' },
  { name: 'Reino Unido', code: 'uk' },
  { name: 'Alemanha', code: 'de' },
  { name: 'França', code: 'fr' },
  { name: 'Espanha', code: 'es' },
  { name: 'Portugal', code: 'pt' },
  { name: 'México', code: 'mx' },
  { name: 'Argentina', code: 'ar' },
  { name: 'Colômbia', code: 'co' },
  { name: 'Chile', code: 'cl' },
  { name: 'Peru', code: 'pe' },
  { name: 'Costa Rica', code: 'cr' },
  { name: 'Venezuela', code: 've' },
  { name: 'Equador', code: 'ec' },
  { name: 'Uruguai', code: 'uy' },
  { name: 'Paraguai', code: 'py' },
  { name: 'Bolívia', code: 'bo' },
  { name: 'Guatemala', code: 'gt' },
  { name: 'Honduras', code: 'hn' },
  { name: 'El Salvador', code: 'sv' },
  { name: 'Nicarágua', code: 'ni' },
  { name: 'Panamá', code: 'pa' },
  { name: 'Cuba', code: 'cu' },
  { name: 'República Dominicana', code: 'do' },
  { name: 'Austrália', code: 'au' },
  { name: 'Índia', code: 'in' },
  { name: 'Japão', code: 'jp' },
  { name: 'China', code: 'cn' },
  { name: 'África do Sul', code: 'za' },
];

/**
 * Mapa de código de país → idioma nativo principal.
 * Para países com múltiplos idiomas oficiais, prioriza o inglês.
 */
export const COUNTRY_LANGUAGE_MAP: Record<string, string> = {
  // Português
  br: 'pt-BR',
  pt: 'pt-BR',

  // Inglês (inclui países multilíngues onde inglês é priorizado)
  us: 'en',
  uk: 'en',
  ca: 'en',   // Canadá: inglês/francês → inglês
  au: 'en',
  za: 'en',   // África do Sul: 11 idiomas oficiais → inglês
  in: 'en',   // Índia: hindi + inglês → inglês (negócios)

  // Espanhol
  es: 'es',
  mx: 'es',
  ar: 'es',
  co: 'es',
  cl: 'es',
  pe: 'es',
  ve: 'es',
  ec: 'es',
  uy: 'es',
  py: 'es',   // Paraguai: espanhol/guarani → espanhol
  bo: 'es',
  gt: 'es',
  hn: 'es',
  sv: 'es',
  ni: 'es',
  pa: 'es',
  cu: 'es',
  do: 'es',
  cr: 'es',

  // Francês
  fr: 'fr',

  // Alemão
  de: 'de',

  // Japonês
  jp: 'ja',

  // Chinês
  cn: 'zh',
};

/**
 * Retorna o código de idioma nativo do país.
 * Para países multilíngues, prioriza o inglês.
 * Retorna null se o país não estiver mapeado (fallback: idioma configurado no agente).
 */
export function getLanguageByCountry(countryCode: string): string | null {
  return COUNTRY_LANGUAGE_MAP[countryCode.toLowerCase()] ?? null;
}

/**
 * Hook para obter o nome do país traduzido.
 * @param code O código de 2 letras do país.
 * @returns O nome traduzido do país.
 */
export const useCountryName = (code: string): string => {
  const { t } = useTranslation();
  const key = `countries_list.${code.toLowerCase()}`;

  const translatedName = t(key, { defaultValue: 'Localização' });

  if (translatedName !== 'Localização') {
    return translatedName;
  }

  const country = COUNTRIES.find(c => c.code === code.toLowerCase());
  return country ? country.name : 'Localização';
};